import { DEFAULT_CURRENCY, type Money, type MoneyInput } from '@/domain/work-order.types'

/** How many minor units make one major unit in `currency` (2 for USD, 0 for JPY). */
export function minorDigits(currency: string): number {
	try {
		return new Intl.NumberFormat('en', { style: 'currency', currency }).resolvedOptions().maximumFractionDigits ?? 2
	} catch {
		return 2
	}
}

/** The ISO 4217 codes the browser knows, for a currency picker. */
export function currencyCodes(): string[] {
	try {
		return Intl.supportedValuesOf('currency')
	} catch {
		return [DEFAULT_CURRENCY]
	}
}

/** Commas as thousands separators only: `1,250` and `12,500,000.50`, never `1,5`. */
const GROUPED = /^\d{1,3}(,\d{3})+(\.\d+)?$/

/**
 * Typed text → money in minor units. Blank is "no value" (`null`); text that is not a
 * non-negative amount with at most the currency's decimals is `'invalid'`, so a form can say
 * so instead of silently rounding what was typed.
 *
 * A comma is accepted only as a thousands separator. Anything else with a comma is refused,
 * so a decimal comma (`1,5`, `12,50`) is never read as a 10× or 100× larger amount.
 */
export function parseMoney(text: string, currency: string): MoneyInput | null | 'invalid' {
	const typed = text.trim()
	if (!typed) return null
	if (typed.includes(',') && !GROUPED.test(typed)) return 'invalid'
	const trimmed = typed.replace(/,/g, '')
	const digits = minorDigits(currency)
	const pattern = digits > 0 ? new RegExp(`^\\d+(\\.\\d{1,${digits}})?$`) : /^\d+$/
	if (!pattern.test(trimmed)) return 'invalid'
	const [whole, fraction = ''] = trimmed.split('.')
	const amountMinor = Number(whole) * 10 ** digits + Number(fraction.padEnd(digits, '0') || '0')
	return Number.isSafeInteger(amountMinor) ? { amountMinor, currency } : 'invalid'
}

/** What a form says when `parseMoney` refuses an amount. */
export const invalidMoneyMessage = (label: string): string =>
	`${label} must be an amount like 1250.50, with a dot for decimals, not negative`

/** Money → the plain amount text an input shows ("1250.50"), '' for none. */
export function moneyInputText(money: Money | null | undefined): string {
	if (!money) return ''
	const digits = minorDigits(money.currency)
	return (money.amountMinor / 10 ** digits).toFixed(digits)
}

/** Money → display text with its currency ("$1,250.50", "€3,000.00"); an em dash for none. */
export function formatMoney(money: Money | null | undefined): string {
	if (!money) return '—'
	const digits = minorDigits(money.currency)
	return new Intl.NumberFormat('en', { style: 'currency', currency: money.currency }).format(
		money.amountMinor / 10 ** digits,
	)
}
